import axios from 'axios'
import { ElMessage } from 'element-plus'
import { getWorkspaceCompanyIdForRequest } from '@/constants/workspaceScope'
import { resolveAppPath } from '@/utils/appPath'
import { notifyQuotaAwareError } from '@/utils/aiRetouchQuotaHint'

/**
 * 规则 A：有无 X-Company-Id 即二分
 * - 公司空间：全量 API 带 X-Company-Id
 * - 个人空间：任何 API 均不带（ACL / grant 由后端按 token 判权）
 */
function resolveRequestCompanyId (config) {
  if (config?.skipCompanyHeader === true) {
    return null
  }
  return getWorkspaceCompanyIdForRequest()
}

/**
 * 从 Laravel 等 JSON/HTML 响应中取出可读错误文案（供 403/404 及兜底分支使用）
 * @param {unknown} data error.response?.data
 * @param {string} fallback
 */
function pickApiErrorMessage (data, fallback) {
  if (data == null) return fallback
  if (typeof data === 'string') {
    const t = data.trim()
    if (!t || t.startsWith('<')) return fallback
    return t.length > 500 ? `${t.slice(0, 500)}…` : t
  }
  if (typeof data === 'object') {
    const m = data.message
    if (typeof m === 'string' && m.trim()) return m.trim()
    if (Array.isArray(m) && m[0] != null) return String(m[0])
  }
  return fallback
}

/**
 * 从 axios 错误回调里调 ElMessage 时偶发不弹出（与 Vue 更新周期叠在一起），延后到微任务更稳定
 * @param {string} text
 */
function notifyApiError (text) {
  const msg = typeof text === 'string' && text.trim() ? text.trim() : '操作失败，请稍后重试'
  if (msg.includes('充值套餐')) {
    notifyQuotaAwareError(msg)
    return
  }
  const run = () => {
    ElMessage.error(msg)
  }
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(run)
  } else {
    setTimeout(run, 0)
  }
}

/**
 * axios 实例
 * Base URL 统一指向后端 Laravel API
 */
const service = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || 'http://192.168.7.32/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

/**
 * 请求拦截器
 * 每次请求前自动从 localStorage 读取 token 并写入 Authorization 头
 */
service.interceptors.request.use(
  (config) => {
    // 上传请求必须让浏览器自行设置 multipart boundary；默认的 JSON 头会使 Laravel 收不到文件字段。
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    /** 公司空间带 X-Company-Id；个人空间不带 */
    const companyId = resolveRequestCompanyId(config)
    if (companyId) {
      config.headers['X-Company-Id'] = companyId
    } else {
      delete config.headers['X-Company-Id']
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * 响应拦截器
 * Laravel 直接返回业务数据，无需判断自定义 code 字段
 * 统一处理 422 表单验证错误、401 未认证等情况
 */
service.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const status = error.response?.status
    const data = error.response?.data

    if (status === 422) {
      // 部分上传/登记流程在业务层自行提示（见 suppress422Toast）
      if (!error.config?.suppress422Toast) {
        const errors = data?.errors
        if (errors) {
          const firstMsg = Object.values(errors)[0]?.[0]
          notifyApiError(firstMsg || data?.message || '输入内容有误，请检查')
        } else {
          notifyApiError(data?.message || '输入内容有误，请检查')
        }
      }
    } else if (status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      const pathName = String(window.location.pathname || '')
      // 公开 H5 / C 端：误打 B 端接口时勿踢到管理端登录
      if (/\/h5(\/|$)/.test(pathName) || /\/c(\/|$)/.test(pathName)) {
        return Promise.reject(error)
      }
      notifyApiError('登录已过期，请重新登录')
      const loginPath = resolveAppPath('/login')
      if (!/\/login\/?$/.test(pathName)) {
        window.location.href = loginPath
      }
    } else if (status === 403) {
      /** GET/POST 等所有方法：无权限统一提示（与后端 message 一致） */
      if (!error.config?.suppress403Toast) {
        notifyApiError(pickApiErrorMessage(data, '您没有权限执行此操作'))
      }
    } else if (status === 404) {
      if (!error.config?.suppress404Toast) {
        notifyApiError(pickApiErrorMessage(data, '请求的资源不存在'))
      }
    } else if (status === 429) {
      notifyApiError('操作过于频繁，请稍后再试')
    } else if (status === 502 || status === 503) {
      /** 喔图/OSS 未配置或上游不可用 */
      if (!error.config?.suppress503Toast) {
        notifyApiError(pickApiErrorMessage(data, '服务暂不可用，请稍后重试'))
      }
    } else if (status === 500) {
      notifyApiError('服务器内部错误，请联系管理员')
    } else {
      notifyApiError(
        pickApiErrorMessage(data, error.message || '网络异常，请稍后重试')
      )
    }

    return Promise.reject(error)
  }
)

export default service
